import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface ActiveMemberState {
  /**
   * Salesforce Contact id of the member the provider is working on, or null before they pick one.
   *
   * The id and nothing else: no name, DOB or MDVIP ID. Those are PHI, and this store is read from
   * devtools in development. Consumers resolve the id through `useActiveMember`.
   */
  activeMemberId: string | null;
}

interface ActiveMemberActions {
  setActiveMember: (contactId: string) => void;
  reset: () => void;
}

type ActiveMemberStore = ActiveMemberState & ActiveMemberActions;

/**
 * The member a provider is currently working on, for this tab only.
 *
 * Deliberately not persisted: `docs/030-state-management.md` forbids member identifiers in
 * `localStorage`/`sessionStorage`, and a URL parameter would leak the id into browser history and
 * telemetry. A full page reload therefore clears the selection, which is accepted — the selection
 * survives in-app navigation, which is what the flow needs.
 */
export const useActiveMemberStore = create<ActiveMemberStore>()(
  devtools(
    (set) => ({
      activeMemberId: null,
      setActiveMember: (contactId) => set({ activeMemberId: contactId }, false, 'activeMember/set'),
      reset: () => set({ activeMemberId: null }, false, 'activeMember/reset'),
    }),
    { name: 'activeMemberStore', enabled: !import.meta.env.PROD },
  ),
);
